import { Grid, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import { get as _get } from 'lodash';
import { useEffect, useState } from 'react';
import { DamageConstants } from '../../../Constants/Constants';
import { DamageTypeOption, DamageTypes } from '../../../types/type';
import FreeForm from '../../shared/FreeformText/Freeform';
import SingleSelect, { SingleSelectStyleInfo } from '../SingleSelect/SingleSelect';
import DamagePicker from './DamagePicker';
import { ThemeStyleInfo } from './DamagePickerTile';

export default function DamagePickerForm(props: any) {
  const theme: Theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const themeBasedStyleInfo: SingleSelectStyleInfo = {
    headerStyle: theme.custom.styles.titleType4,
    descriptionStyle: theme.custom.styles.primaryDescription,
    optionStyle: {
      ...theme.custom.styles.primaryDescription,
      radioButtonColor: theme.custom.colors.radioButtonColor,
      radioButtonSelectedColor: theme.custom.colors.radioButtonSelectedColor,
      borderColor: theme.custom.styles.showBorderAroundListItems
        ? `${theme.custom.styles.listItemBorderDefaultStyle} ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
        : theme.custom.colors.bodyColor,
      selectedBorderColor: `${theme.custom.styles.listItemBorderSelectedStyle} ${theme.custom.styles.radioandcheckboxStyle.selectedBorder}`,
      optionHeight: theme.custom.styles.defaultInputHeight,
    },
  };
  const pickerStyle: ThemeStyleInfo = {
    tileImageStyle: {
      selectedFillColor: theme.custom.styles.damagePicker.selectedFill,
      notSelectedFillColor: '#FFF',
      strokeColor: theme.custom.styles.damagePicker.stroke,
      hoverFillColor: theme.custom.styles.damagePicker.hoverFill,
    },
    tileTitleStyle: {
      textFontFamily: theme.custom.styles.body2.fontFamily,
      textFontWeight: theme.custom.styles.body2.fontWeight,
      textFontSize: `${theme.custom.styles.body2.fontSize}px`,
      textFontSizeToFit: '14px',
      textColor: theme.custom.styles.body2.color,
      selectedTextFontWeight: theme.custom.styles.body2Bold.fontWeight,
    },
    outLineStyle: {
      fillColor: theme.custom.styles.damagePicker.outline,
    },
  };
  // Damage Picker Form - Single Select Options
  const [damagePickerFormSingleSelectOptions, setDamagePickerFormSingleSelectOptions] = useState<DamageTypeOption[]>(
    getOptionsForSingleSelect(_get(props, 'preFilledDamagePickerData.selectedDamageTypeOption'))
  );
  // Damage Picker Form - Selected Option
  const [selectedDamageTypeOption, setSelectedDamageTypeOption] = useState(
    _get(props, 'preFilledDamagePickerData.selectedDamageTypeOption')
  );
  // Damage Picker Form - Damaged parts
  const [selectedDamagePickerParts, setSelectedDamagePickerParts] = useState<string[]>(
    mapDamagePartCodes(_get(props, 'preFilledDamagePickerData.selectedDamageCodes', []))
  );
  // Damage description
  const [damageDescription, setDamageDescription] = useState(
    _get(props, 'preFilledDamagePickerData.damageDescription')
  );

  const { handleDamagePickerDataUpdate } = props;
  useEffect(() => {
    handleDamagePickerDataUpdate({
      selectedDamageTypeOption: selectedDamageTypeOption,
      selectedDamageCodes: selectedDamagePickerParts,
      damageDescription: damageDescription,
    });
  }, [selectedDamagePickerParts, handleDamagePickerDataUpdate, selectedDamageTypeOption, damageDescription]);

  function mapDamagePartCodes(codes: any) {
    let partCodes: string[] = [];
    partCodes = codes.map((code: any) => {
      return code.vehicleDamage;
    });
    return partCodes;
  }

  function getOptionsForSingleSelect(damageType: string) {
    let localDamageOptions = [
      { id: 'noDamage', text: DamageConstants.NO_DAMAGE, isSelected: false },
      {
        id: 'partialDamage',
        text: DamageConstants.SOME_DAMAGE,
        isSelected: false,
      },
      { id: 'totalLoss', text: DamageConstants.ALL_DAMAGE, isSelected: false },
    ];
    if (damageType != null) {
      localDamageOptions = localDamageOptions.map((option: any) => {
        if (option.id === damageType) {
          return { ...option, isSelected: true };
        } else {
          return { ...option, isSelected: false };
        }
      });
    }
    return localDamageOptions;
  }

  function handleSingleSelectionChange(damageType: string) {
    setSelectedDamageTypeOption(damageType);
  }

  function handleDamagePickerSelectionChange(parts: string[]) {
    setSelectedDamagePickerParts(parts);
  }
  function handleDamageDescriptionUpdate(description: string) {
    setDamageDescription(description);
  }

  return (
    <Grid container spacing={2}>
      <Grid sx={{ marginTop: '1em' }} item xs={12}>
        <SingleSelect
          selectionChangeHandler={handleSingleSelectionChange}
          styleInfo={themeBasedStyleInfo}
          options={damagePickerFormSingleSelectOptions}
          description=""
          header={'Please select the damage to your vehicle.'}
        ></SingleSelect>
      </Grid>
      <Grid sx={{ marginTop: '1em' }} item xs={12}>
        {selectedDamageTypeOption === DamageTypes.PARTIAL_DAMAGE && (
          <Grid container item>
            <Grid item xs={12}>
              <Typography variant="body1">
                Select the damaged areas by clicking on them. If something is missing from the chart or you would like
                to provide additional information, please use the Additional Damage text field below.
              </Typography>
            </Grid>
            <Grid item sx={{ marginBottom: '.5em' }} xs={12}>
              <DamagePicker
                selectedDamageCodes={selectedDamagePickerParts}
                themeInfo={pickerStyle}
                selectionChangeHandler={handleDamagePickerSelectionChange}
                isMobile={isMobile}
              ></DamagePicker>
            </Grid>
            <Grid
              sx={{
                marginTop: '1em',
                marginBottom: '1em',
              }}
              item
              xs={12}
            >
              {selectedDamageTypeOption === DamageTypes.PARTIAL_DAMAGE && (
                <FreeForm
                  prefillValue={damageDescription}
                  selectionChangeHandler={handleDamageDescriptionUpdate}
                  styleInfo={themeBasedStyleInfo}
                  description={''}
                  header={'Additional Damage'}
                ></FreeForm>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
